import { __decorate } from "tslib";
import { LitElement, html, css, customElement } from 'lit-element';
import { property } from '@cds/core/internal';
import { classMap } from 'lit-html/directives/class-map';
import { styleMap } from 'lit-html/directives/style-map.js';
import { baseStyles } from './styles';
let CdsGridColumn = class CdsGridColumn extends LitElement {
    constructor() {
        super(...arguments);
        this.sort = null;
        this.hidden = null;
        this.position = 'initial';
        this.resizable = true;
        this.slot = 'columns';
        this.col = null;
        this.width = null;
    }
    static get styles() {
        return [
            baseStyles,
            css `
        :host {
          --width: initial;
          display: contents;
        }

        .line {
          width: var(--cds-global-space-4);
          height: var(--cds-global-space-7);
          align-self: center;
          padding: 0;
          border: 0;
          background: none;
          border-right: var(--cds-alias-object-border-width-100) solid var(--cds-alias-object-border-color);
        }

        button.line {
          cursor: ew-resize;
        }

        .line:focus {
          outline: 0;
          border-right: var(--cds-alias-object-border-width-100) solid var(--cds-alias-status-info);
        }

        .sort {
          display: flex;
          flex-direction: column;
          font-size: 10px;
          align-self: stretch;
          align-items: center;
          justify-content: center;
          color: var(--cds-global-typography-color-200);
          width: var(--cds-global-space-8);
          padding: 0;
          background: transparent;
          border: 0;
          cursor: pointer;
        }

        .ascending .up,
        .descending .down {
          color: var(--cds-global-typography-color-500);
        }

        th {
          border: 0;
          font-weight: 500;
          line-height: 0;
          color: var(--cds-global-typography-color-500);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 14px;
          text-align: left;
          line-height: 1em;
          margin: 0;
          display: flex;
          gap: var(--cds-global-space-3);
          padding: var(--cds-global-space-5) 0 var(--cds-global-space-5) var(--cds-global-space-6);
          min-height: var(--cds-global-space-11);
          align-items: center;
          height: 100%;
          border-bottom: var(--cds-alias-object-border-width-100) solid var(--cds-alias-object-border-color);
          top: 0;
          background: var(--cds-alias-object-container-background);
          position: sticky !important;
          z-index: 99;
        }

        :host([position=fixed]) th,
        :host([position=sticky]) th {
          z-index: 999;
        }

        ::slotted([cds-layout='align:right']) { /* temp fix waiting on next release */
          margin-left: auto;
        }

        ::slotted(cds-inline-button) {
          --color: var(--cds-global-typography-color-500);
          --padding: 0;
        }

        [cds-layout='horizontal fill'] {
          display: flex;
          width: 100%;
        }
      `
        ];
    }
    get th() {
        return this.shadowRoot.querySelector('th');
    }
    render() {
        return html `
        <th
          aria-colindex=${this.col + 1}
          style=${styleMap({ left: `var(--col-${this.col}-left, initial)` })}>
          <div cds-layout="horizontal fill">
            <slot></slot>
          </div>

          ${this.sort ? html `
          <button
            aria-label="sort column"
            @click=${this.sortClick}
            class=${classMap({
            sort: true,
            ascending: this.sort === "ascending",
            descending: this.sort === "descending"
        })}>
            <span class="up">&#9650;</span>
            <span class="down">&#9660;</span>
          </button>
          ` : ''}
          ${this.resizable ? html `<button type="button" id="line" class="line" aria-label="resize column"></button>` : html `<div class="line"></div>`}
        </th>
    `;
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.listenForMouseResize();
        this.listenForKeyboardResize();
    }
    updated(props) {
        super.updated(props);
        if (props.has('hidden') && this.hidden !== null) {
            this.dispatchEvent(new CustomEvent('hiddenChange', { bubbles: true }));
        }
        if (props.has('position') && this.position !== props.get('position') && this.position !== null) {
            this.dispatchEvent(new CustomEvent('positionChange', { bubbles: true }));
        }
    }
    sortClick() {
        let sort = this.sort;
        switch (sort) {
            case "ascending":
                sort = "descending";
                break;
            case "descending":
                sort = "none";
                break;
            case "none":
                sort = "ascending";
        }
        this.dispatchEvent(new CustomEvent('sortChange', { detail: sort }));
    }
    listenForMouseResize() {
        const host = this;
        let m_pos;
        const button = this.shadowRoot.querySelector('#line');
        button.addEventListener('mousedown', (e) => {
            m_pos = e.x;
            document.addEventListener('mousemove', resize, false);
        }, false);
        document.addEventListener('mouseup', () => {
            document.removeEventListener('mousemove', resize, false);
        }, false);
        function resize(e) {
            requestAnimationFrame(() => {
                const dx = m_pos - e.x;
                m_pos = e.x;
                host.parentElement.style.setProperty(`--col-${host.col}-width`, (parseInt(getComputedStyle(host.th, '').width) - dx) + 'px');
            });
        }
    }
    listenForKeyboardResize() {
        this.shadowRoot.querySelector('#line').addEventListener('keydown', (e) => {
            this.scrollIntoView(false);
            if (e.code === 'ArrowLeft') {
                e.preventDefault();
                this.resize(-10);
            }
            if (e.code === 'ArrowRight') {
                e.preventDefault();
                this.resize(24); // offset negative margin
            }
        });
    }
    resize(size) {
        this.parentElement.style.setProperty(`--col-${this.col}-width`, this.width ? `${this.width}px` : `${Math.max(100, parseInt(getComputedStyle(this.shadowRoot.querySelector('th')).width)) + size}px`);
    }
};
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "sort", void 0);
__decorate([
    property({ type: Boolean })
], CdsGridColumn.prototype, "hidden", void 0);
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "position", void 0);
__decorate([
    property({ type: Boolean })
], CdsGridColumn.prototype, "resizable", void 0);
__decorate([
    property({ type: String, attribute: 'slot', reflect: true })
], CdsGridColumn.prototype, "slot", void 0);
__decorate([
    property({ type: Number })
], CdsGridColumn.prototype, "col", void 0);
__decorate([
    property({ type: String })
], CdsGridColumn.prototype, "width", void 0);
CdsGridColumn = __decorate([
    customElement('cds-grid-column')
], CdsGridColumn);
export { CdsGridColumn };
//# sourceMappingURL=grid-column.element.js.map